import { useCallback, useRef, useState } from "react";
import { describeImage } from "@/content/image-descriptions";
import { resolveDescribedImage } from "@/lib/describedImages";

type MediaImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "onLoad" | "onError"> & {
  src: string;
  alt: string;
  /** Extra classes appended to `demo-image-media img-fluid`. */
  imgClassName?: string;
  /** Called after the image finishes loading (or fallback resolves). */
  onLoaded?: () => void;
  /** Optional fallback swapped in if the primary src errors. */
  fallbackSrc?: string;
};

/**
 * Reusable image wrapper that reserves layout space via its parent frame's
 * aspect-ratio and paints a shimmer skeleton until the image decodes. Keeps
 * layout stable and avoids the "blank frame" flash while media loads.
 *
 * Must be rendered inside a positioned parent (e.g. `.demo-image-frame`) so
 * the absolute skeleton overlay clips correctly.
 */
export function MediaImage({
  src,
  alt,
  imgClassName,
  onLoaded,
  fallbackSrc,
  ...imgProps
}: MediaImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const fallbackAppliedRef = useRef(false);

  // Ref callback runs synchronously on mount with the real <img> element and
  // handles the cached-image case where `onLoad` never fires because the
  // browser resolved the src before React attached the listener. This avoids
  // relying on useEffect timing during SSR hydration. It also covers the
  // matching failure case: on an SSR'd page the browser can attempt (and
  // fail) the request before hydration attaches `onError`, so that event is
  // missed entirely — `complete && naturalHeight === 0` detects that already-
  // failed state and applies the fallback here instead.
  const setImgRef = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img || !img.complete) return;
      if (img.naturalHeight > 0) {
        setIsLoaded(true);
        onLoaded?.();
        return;
      }
      if (fallbackSrc && !fallbackAppliedRef.current) {
        fallbackAppliedRef.current = true;
        img.src = fallbackSrc;
        return;
      }
      setIsLoaded(true);
    },
    [fallbackSrc, onLoaded],
  );

  const handleLoad = () => {
    setIsLoaded(true);
    onLoaded?.();
  };

  const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (fallbackSrc && !fallbackAppliedRef.current) {
      fallbackAppliedRef.current = true;
      event.currentTarget.src = fallbackSrc;
      return;
    }
    // Give up gracefully so the skeleton doesn't spin forever on hard failure.
    setIsLoaded(true);
  };

  return (
    <>
      {/* Skeleton sits BEHIND the image (z-index: 0) so a stale isLoaded
          state can never mask a decoded image. It fades out once the image
          reports it has loaded. */}
      <span
        className="demo-image-skeleton"
        aria-hidden="true"
        style={{ zIndex: 0, opacity: isLoaded ? 0 : 1, transition: "opacity 0.3s ease" }}
      />
      <img
        ref={setImgRef}
        src={resolveDescribedImage(src)}
        alt={describeImage(resolveDescribedImage(src), alt)}
        title={imgProps.title ?? describeImage(resolveDescribedImage(src), alt)}
        data-name={(imgProps as Record<string, unknown>)["data-name"] ?? alt}
        loading={imgProps.loading ?? "lazy"}
        decoding={imgProps.decoding ?? "async"}
        {...imgProps}
        className={["demo-image-media img-fluid", isLoaded ? "is-loaded" : "", imgClassName]
          .filter(Boolean)
          .join(" ")}
        onLoad={handleLoad}
        onError={handleError}
      />
    </>
  );
}
